import { useMemo, useState } from 'react'

import { useCreativeLibrary } from '../../demo/selectors'
import { demoProducts, useDemoStore } from '../../demo/store'
import type { CreativeFilter, CreativeLibraryItem, CreativeSource, CreativeKind } from '../../demo/types'
import { Button, CreativeCard, EmptyState, Icon, Modal } from '../../ui'
import { CreativeLightbox } from './CreativeLightbox'
import { creativeCopy } from './copy'
import styles from './CreativePickerDialog.module.css'

interface CreativePickerDialogProps {
  open: boolean
  campaignId: string
  productId: string
  attachedIds: string[]
  mode: 'multi' | 'single'
  onClose: () => void
  onToast: (message: string) => void
  onSelect?: (creativeId: string) => void
}

export function CreativePickerDialog({ open, campaignId, productId, attachedIds, mode, onClose, onToast, onSelect }: CreativePickerDialogProps) {
  const [source, setSource] = useState<CreativeSource | 'all'>('all')
  const [kind, setKind] = useState<CreativeKind | 'all'>('all')
  const [usage, setUsage] = useState<'all' | 'used' | 'unused'>('all')
  const [viewProduct, setViewProduct] = useState(productId)
  const [q, setQ] = useState('')
  const [chosen, setChosen] = useState<string[]>([])
  const [previewId, setPreviewId] = useState<string | null>(null)
  const filter = useMemo<CreativeFilter>(() => ({ source, kind, usage, productId: viewProduct || undefined, q }), [kind, q, source, usage, viewProduct])
  const library = useCreativeLibrary(filter)
  const allFilter = useMemo(() => ({}), [])
  const allLibrary = useCreativeLibrary(allFilter)
  const attachCreatives = useDemoStore((state) => state.attachCreatives)
  const preview = allLibrary.find((item) => item.id === previewId) ?? null

  const toggle = (creative: CreativeLibraryItem) => {
    if (creative.productId !== productId || attachedIds.includes(creative.id)) return
    setChosen((current) => mode === 'single' ? (current[0] === creative.id ? [] : [creative.id]) : current.includes(creative.id) ? current.filter((id) => id !== creative.id) : [...current, creative.id])
  }
  const confirm = () => {
    if (!chosen.length) return
    if (mode === 'single') onSelect?.(chosen[0]!)
    else attachCreatives(campaignId, chosen)
    onToast(mode === 'single' ? '已选择素材' : `已添加 ${chosen.length} 条素材，勾选后即可投放`)
    onClose()
  }

  return (
    <div className={styles.host}>
      <Modal open={open} title={mode === 'multi' ? '从素材库添加' : '选择素材'} hint="浏览素材库并选择适合当前商品的素材" onClose={onClose} footer={<><Button variant="ghost" onClick={onClose}>取消</Button><Button disabled={!chosen.length} onClick={confirm}>{mode === 'multi' ? `添加 ${chosen.length} 条` : '选择素材'}</Button></>}>
        <div className={styles.body}>
          <div className={styles.toolbar}>
            <select aria-label="商品" value={viewProduct} onChange={(event) => setViewProduct(event.target.value)}><option value="">全部商品</option>{demoProducts.map((product) => <option key={product.id} value={product.id}>{product.name}</option>)}</select>
            <select aria-label="来源" value={source} onChange={(event) => setSource(event.target.value as CreativeSource | 'all')}><option value="all">全部来源</option><option value="ai">AI 生成</option><option value="upload">我的上传</option><option value="kol">达人授权</option></select>
            <select aria-label="类型" value={kind} onChange={(event) => setKind(event.target.value as CreativeKind | 'all')}><option value="all">全部类型</option><option value="video">视频</option><option value="image">图片</option></select>
            <select aria-label="使用状态" value={usage} onChange={(event) => setUsage(event.target.value as typeof usage)}><option value="all">全部状态</option><option value="used">使用中</option><option value="unused">未使用</option></select>
            <label className={styles.search}><Icon name="search" size={17} /><input value={q} placeholder={creativeCopy.searchPlaceholder} onChange={(event) => setQ(event.target.value)} /></label>
          </div>
          {library.length ? <div className={styles.grid}>{library.map((creative) => {
            const attached = attachedIds.includes(creative.id)
            const incompatible = creative.productId !== productId
            const disabled = attached || incompatible
            const productName = demoProducts.find((product) => product.id === creative.productId)?.name ?? creative.productId
            const title = incompatible ? `这条素材属于「${productName}」，不能用在本广告任务` : attached ? '已在本广告里' : undefined
            return <div key={creative.id} className={`${styles.item} ${chosen.includes(creative.id) ? styles.chosen : ''} ${disabled ? styles.disabled : ''}`} title={title}>
              <CreativeCard name={creative.name} kind={creative.kind} cover={creative.cover} dataUrl={creative.dataUrl} duration={creative.duration} source={creative.source} kol={creative.kol} usedByAds={creative.usedByAds} compliance={creative.compliance} showUsage onClick={() => setPreviewId(creative.id)} />
              <button type="button" className={styles.check} disabled={disabled} aria-label={attached ? '已在本广告里' : '选择素材'} onClick={() => toggle(creative)}>{attached || chosen.includes(creative.id) ? <Icon name="check" size={14} /> : null}</button>
              {attached ? <span className={styles.attached}>已在本广告里</span> : null}
            </div>
          })}</div> : <EmptyState title={creativeCopy.noMatches} text={creativeCopy.noMatchesText} />}
        </div>
      </Modal>
      {preview ? <CreativeLightbox key={preview.id} creative={preview} onClose={() => setPreviewId(null)} onUse={(creative) => { toggle(creative); setPreviewId(null) }} onToast={onToast} /> : null}
    </div>
  )
}
